/**
 * AI-Guided Treatment Plan Generator via Medplum Bot (`ai-treatment-plan-generator`).
 *
 * Sends the provider's temporary in-memory conversation to the generator bot and
 * validates the returned union at runtime. The bot never writes FHIR resources;
 * the frontend applies validated operations and creates the draft QR itself.
 */

import { ContentType } from '@medplum/core';
import type { MedplumClient } from '@medplum/core';
import type { Identifier } from '@medplum/fhirtypes';
import type { ChatChecklistPart } from '../features/ai-assistant/chat-artifacts/chatMessageParts';
import type { TreatmentPlanRevisionOperation } from './treatmentPlanAiRevision';
import type {
  EvidenceGroundedSymptom,
  TreatmentPlanClinicalContextDiagnosis,
  TreatmentPlanOperation,
} from './treatment-plan/evidenceGroundedSymptoms';

export const AI_TREATMENT_PLAN_GENERATOR_BOT_IDENTIFIER = {
  system: 'https://unify.health/bots',
  value: 'ai-treatment-plan-generator',
} as const;

export function getAiTreatmentPlanGeneratorBotExecuteTarget(): string | Identifier {
  const id = import.meta.env.VITE_MEDPLUM_AI_TREATMENT_PLAN_GENERATOR_BOT_ID?.trim();
  if (id) return id;
  const system =
    import.meta.env.VITE_MEDPLUM_AI_TREATMENT_PLAN_GENERATOR_BOT_IDENTIFIER_SYSTEM?.trim()
    ?? AI_TREATMENT_PLAN_GENERATOR_BOT_IDENTIFIER.system;
  const value =
    import.meta.env.VITE_MEDPLUM_AI_TREATMENT_PLAN_GENERATOR_BOT_IDENTIFIER_VALUE?.trim()
    ?? AI_TREATMENT_PLAN_GENERATOR_BOT_IDENTIFIER.value;
  return { system, value };
}

export type TreatmentPlanGeneratorAction = 'chat' | 'generate';
export type TreatmentPlanGeneratorMode = 'guided' | 'quick';

export interface TreatmentPlanGeneratorMessage {
  role: 'provider' | 'assistant';
  content: string;
}

/** Best-effort clinical context from the live treatment-plan form; bot uses it to avoid re-asking known facts. */
export interface TreatmentPlanGeneratorClinicalContext {
  diagnoses?: TreatmentPlanClinicalContextDiagnosis[];
  symptoms?: EvidenceGroundedSymptom[];
  treatmentFrequency?: string;
}

export interface TreatmentPlanGeneratorInput {
  action: TreatmentPlanGeneratorAction;
  patientId: string;
  episodeId: string;
  questionnaireId: string;
  messages: TreatmentPlanGeneratorMessage[];
  /** Whether this is a first-time plan, revision of an existing plan, etc. */
  operation?: TreatmentPlanOperation;
  /** guided (default) or quick documentation-first full generation. */
  mode?: TreatmentPlanGeneratorMode;
  clinicalContext?: TreatmentPlanGeneratorClinicalContext;
}

/** Generation reuses the revision operation model — applied with the same machinery. */
export type TreatmentPlanGenerationOperation = TreatmentPlanRevisionOperation;

export interface TreatmentPlanGeneratorChatResult {
  type: 'chat';
  assistantMessage: string;
  suggestedReadyToGenerate: boolean;
  missingTopics?: string[];
  /** Short reply options the provider can tap instead of typing. Kept for backward compatibility. */
  suggestedResponses?: string[];
  /** Structured checklist for the provider to respond with, when the bot supplies one. */
  checklist?: ChatChecklistPart;
}

export interface TreatmentPlanGeneratorNeedsInformationResult {
  type: 'needs-information';
  assistantMessage: string;
  missingTopics: string[];
}

export interface TreatmentPlanGeneratorSuccessResult {
  type: 'generated';
  operations: TreatmentPlanGenerationOperation[];
  summary: string;
  warnings?: string[];
  model?: string;
  promptVersion: string;
}

export type TreatmentPlanGeneratorResult =
  | TreatmentPlanGeneratorChatResult
  | TreatmentPlanGeneratorNeedsInformationResult
  | TreatmentPlanGeneratorSuccessResult;

function isStringArray(raw: unknown): raw is string[] {
  return Array.isArray(raw) && raw.every((v) => typeof v === 'string');
}

/** Runtime validation for a single checklist part — bot output is untrusted until validated. */
function parseChatChecklistPart(raw: unknown): ChatChecklistPart | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const candidate = raw as Record<string, unknown>;
  if (
    typeof candidate.id !== 'string'
    || typeof candidate.prompt !== 'string'
    || !Array.isArray(candidate.options)
    || (candidate.selectionMode !== 'single' && candidate.selectionMode !== 'multiple')
  ) {
    return undefined;
  }
  const options = candidate.options.filter(
    (option): option is { id: string; label: string } =>
      Boolean(option)
      && typeof option === 'object'
      && typeof (option as Record<string, unknown>).id === 'string'
      && typeof (option as Record<string, unknown>).label === 'string',
  );
  if (options.length === 0) return undefined;
  const clinicalField =
    typeof candidate.clinicalField === 'string'
    && ['symptoms', 'goals', 'objectives', 'interventions', 'other'].includes(candidate.clinicalField)
      ? (candidate.clinicalField as ChatChecklistPart['clinicalField'])
      : undefined;
  return {
    type: 'checklist',
    id: candidate.id,
    prompt: candidate.prompt,
    selectionMode: candidate.selectionMode,
    options,
    minSelections: typeof candidate.minSelections === 'number' ? candidate.minSelections : undefined,
    maxSelections: typeof candidate.maxSelections === 'number' ? candidate.maxSelections : undefined,
    allowOther: candidate.allowOther === true,
    otherLabel: typeof candidate.otherLabel === 'string' ? candidate.otherLabel : undefined,
    allowSkip: candidate.allowSkip === true,
    submitLabel: typeof candidate.submitLabel === 'string' ? candidate.submitLabel : undefined,
    ...(clinicalField ? { clinicalField } : {}),
  };
}

/** Runtime validation of the bot result union — bot output is untrusted until validated. */
export function parseTreatmentPlanGeneratorResult(raw: unknown): TreatmentPlanGeneratorResult {
  if (!raw || typeof raw !== 'object') {
    throw new Error('AI treatment plan generator bot did not return a result');
  }
  const result = raw as Record<string, unknown>;
  switch (result.type) {
    case 'chat': {
      if (typeof result.assistantMessage !== 'string' || !result.assistantMessage.trim()) {
        throw new Error('Generator chat result missing assistantMessage');
      }
      return {
        type: 'chat',
        assistantMessage: result.assistantMessage,
        suggestedReadyToGenerate: result.suggestedReadyToGenerate === true,
        missingTopics: isStringArray(result.missingTopics) ? result.missingTopics : undefined,
        suggestedResponses: isStringArray(result.suggestedResponses)
          ? result.suggestedResponses.slice(0, 4)
          : undefined,
        checklist: parseChatChecklistPart(result.checklist),
      };
    }
    case 'needs-information': {
      if (typeof result.assistantMessage !== 'string' || !result.assistantMessage.trim()) {
        throw new Error('Generator needs-information result missing assistantMessage');
      }
      return {
        type: 'needs-information',
        assistantMessage: result.assistantMessage,
        missingTopics: isStringArray(result.missingTopics) ? result.missingTopics : [],
      };
    }
    case 'generated': {
      if (typeof result.summary !== 'string' || !Array.isArray(result.operations)) {
        throw new Error('Generator result missing summary or operations');
      }
      if (typeof result.promptVersion !== 'string' || !result.promptVersion.trim()) {
        throw new Error('Generator result missing promptVersion');
      }
      return {
        type: 'generated',
        operations: result.operations as TreatmentPlanGenerationOperation[],
        summary: result.summary,
        warnings: isStringArray(result.warnings) ? result.warnings : undefined,
        model: typeof result.model === 'string' ? result.model : undefined,
        promptVersion: result.promptVersion,
      };
    }
    default:
      throw new Error('AI treatment plan generator bot returned an unknown result type');
  }
}

export async function requestAiTreatmentPlanGenerator(
  medplum: MedplumClient,
  input: TreatmentPlanGeneratorInput,
): Promise<TreatmentPlanGeneratorResult> {
  const result = await medplum.executeBot(
    getAiTreatmentPlanGeneratorBotExecuteTarget(),
    input,
    ContentType.JSON,
  );
  return parseTreatmentPlanGeneratorResult(result);
}
